// Toppers order block, rendered by both the account order-detail page and the
// thank-you page: the delivery + products sections, the order-offer islands,
// and the Order Summary panel (print button, Bill to / Payment columns, and
// the itemized breakdown).
// Consumes the typed OrderDetailData serialization and reproduces the legacy
// toppers order_detail_partial markup verbatim -- distinct from the shared
// OrderDetail `.details` layout (different classes: is-checked progress steps,
// the product__price-container price block, the __breakdown summary).
import type { Immutable } from "immer";
import React from "react";
import { OrderOfferImage } from "tsi-common-react/src/components/OrderOfferImage/index";
import { createOrderOfferTemplate } from "tsi-common-react/src/templates/OrderOfferTemplate";
import { t } from "ttag";

import type { templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { OrderOffer } from "../../apps/checkout/OrderOffer";

type IData = Immutable<
    NonNullable<templates.OrderDetailDocumentTemplate["order_detail"]>
>;
type ISection = IData["shipped_sections"][number];
type ILine = ISection["lines"][number];
type IProgressBar = ISection["progress_bar"];
type IShippingTo = ISection["shipping_to"];
type IOffer = IData["offers"][number];

const OrderOfferTemplate = createOrderOfferTemplate({
    OrderOffer,
    OrderOfferImage,
});

const ShippingTo = (
    props: Immutable<{ shippingTo: IShippingTo }>,
): React.JSX.Element => {
    const { shippingTo } = props;
    return (
        <>
            <h2 className="delivery__results__title">{shippingTo.status}</h2>
            <p className="delivery__results__address">
                To: {shippingTo.name}, {shippingTo.line1},{" "}
                {shippingTo.line2 && <>{shippingTo.line2}, </>}
                {shippingTo.line4}, {shippingTo.state} {shippingTo.postcode}
            </p>
        </>
    );
};

const ProgressBar = (
    props: Immutable<{ progressBar: IProgressBar }>,
): React.JSX.Element => {
    const { progressBar } = props;
    const threeStep = (
        preparingChecked: boolean,
        shippedChecked: boolean,
    ): React.JSX.Element => (
        <ul className="progress-bar__list">
            <li className="progress-bar__list-item is-checked">
                <span className="item__title">Order Received</span>
            </li>
            <li
                className={`progress-bar__list-item${preparingChecked ? " is-checked" : ""}`}
            >
                <span className="item__title">Preparing</span>
            </li>
            <li
                className={`progress-bar__list-item${shippedChecked ? " is-checked" : ""}`}
            >
                <span className="item__title">Shipped</span>
            </li>
        </ul>
    );
    return (
        <div className="progress-bar">
            {progressBar.step === "received" && threeStep(false, false)}
            {progressBar.step === "preparing" && threeStep(true, false)}
            {progressBar.step === "shipped" && threeStep(true, true)}
            {progressBar.step === "error" && (
                <ul className="progress-bar__list">
                    <li className="progress-bar__list-item is-error">
                        <span className="item__title">
                            {progressBar.error_label}
                        </span>
                    </li>
                </ul>
            )}
        </div>
    );
};

const OrderLine = (props: Immutable<{ line: ILine }>): React.JSX.Element => {
    const { line } = props;
    const { price } = line;
    return (
        <>
            <div className="section__left products__results">
                <div className="product__image">
                    {/* Need link title since content is an image. See #13290 */}
                    <a href={line.product_link}>
                        <img
                            src={line.image_url}
                            alt={line.image_alt}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
                <div className="product__details">
                    {/* The legacy partial nested this price block inside the
                        <p>; a browser auto-splits that into siblings, so render
                        it as a sibling here to match the real DOM (and avoid a
                        <div>-in-<p> hydration mismatch). */}
                    <p className="product__details__copy">
                        {line.title}
                        <br />
                        {line.attribute_summary}
                        <br />
                        Qty: {line.quantity}
                        <br />
                    </p>
                    <div className="product__price-container">
                        {price.is_discounted ? (
                            <>
                                <span className="product__actual-price">
                                    {price.actual_display}
                                </span>
                                {price.addon_display && (
                                    <span className="product__retail-price">
                                        +{price.addon_display}
                                    </span>
                                )}
                                <span className="product__retail-price product__retail-price--strike">
                                    {price.msrp_display}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="product__retail-price">
                                    {price.actual_display}
                                </span>
                                {price.addon_display && (
                                    <span className="product__retail-price">
                                        +{price.addon_display}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="section__right products__status">
                <p className="products__status__copy">
                    Item Status: {line.item_status}
                    <br />
                    Shipping Method: {line.shipping_method_label}
                    <br />
                    {line.tracking_number && (
                        <>Tracking Number: {line.tracking_number}</>
                    )}
                </p>
            </div>
        </>
    );
};

const OrderSection = (
    props: Immutable<{ section: ISection }>,
): React.JSX.Element => {
    const { section } = props;
    return (
        <>
            <div className="section delivery">
                <div className="section__left delivery__results">
                    <ShippingTo shippingTo={section.shipping_to} />
                </div>
                <div className="section__right delivery__status">
                    <ProgressBar progressBar={section.progress_bar} />
                </div>
            </div>
            <div className="section products">
                {section.lines.map((line, i) => (
                    <OrderLine key={i} line={line} />
                ))}
            </div>
        </>
    );
};

const OrderOffers = (
    props: Immutable<{ offers: readonly IOffer[] }>,
): React.JSX.Element | null => {
    const { offers } = props;
    if (offers.length === 0) {
        return null;
    }
    return (
        <>
            {offers.map((offer, i) => (
                <div key={i} className="thank-you-order-offer l-capped-width">
                    <OrderOfferTemplate {...offer} />
                </div>
            ))}
        </>
    );
};

const PrintIcon = (): React.JSX.Element => (
    <>
        <span className="ada-screenreader-only">Print icon</span>
        <svg
            className="order-summary-details__print-icon"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            data-name="Layer 1"
            viewBox="0 0 91 88.66"
        >
            <path d="M75.67 38.83H61.34v-5.34h14.33zm2-9.34H59.34a2 2 0 0 0-2 2v9.34a2 2 0 0 0 2 2h18.33a2 2 0 0 0 2-2v-9.34a2 2 0 0 0-2-2M29.5 78.33h32a2 2 0 0 0 0-4h-32a2 2 0 0 0 0 4m0-10.67h32a2 2 0 0 0 0-4h-32a2 2 0 0 0 0 4m57.5-9a3 3 0 0 1-3 3h-8.67v-6.33h2.34a2 2 0 0 0 0-4H13.33a2 2 0 0 0 0 4h3.34v6.33H7a3 3 0 0 1-3-3V25a3 3 0 0 1 3-3h77a3 3 0 0 1 3 3zm-15.67 26H20.67V55.33h50.66zM11.06 4h68.88v14H11.06zM84 18h-.06V2a2 2 0 0 0-2-2H9.06a2 2 0 0 0-2 2v16H7a7 7 0 0 0-7 7v33.66a7 7 0 0 0 7 7h9.67v21a2 2 0 0 0 2 2h54.66a2 2 0 0 0 2-2v-21H84a7 7 0 0 0 7-7V25a7 7 0 0 0-7-7" />
        </svg>
    </>
);

export const OrderDetail = (
    props: Immutable<{ data: IData }>,
): React.JSX.Element => {
    const { data } = props;
    return (
        <div className="order-summary">
            {data.shipped_sections.map((section, i) => (
                <OrderSection key={i} section={section} />
            ))}

            <OrderSection section={data.unshipped_section} />

            <OrderOffers offers={data.offers} />

            <div className="order-summary-details details">
                <div className="order-summary-details__column-header">
                    <div className="order-summary-details__title">
                        Order Summary
                    </div>
                    <button
                        className="order-summary-details__print"
                        onClick={() => {
                            getWindow()?.print();
                        }}
                    >
                        <div className="order-summary-details__print-copy">
                            Print
                        </div>
                        <div className="order-summary-details__print-icon-container">
                            <PrintIcon />
                        </div>
                    </button>
                </div>
                <div className="order-summary-details__columns">
                    <div className="order-summary-details__column">
                        <h2 className="order-summary-details__column__title">
                            Bill to
                        </h2>
                        <div className="order-summary-details__copy-block order-summary-details__address">
                            <div>{data.billing_name}</div>
                            <div>
                                {data.billing_line1}
                                {data.billing_line2 && (
                                    <>, {data.billing_line2}</>
                                )}
                                {data.billing_line3 && (
                                    <>, {data.billing_line3}</>
                                )}
                            </div>
                            <div>
                                {data.billing_line4}, {data.billing_state}{" "}
                                {data.billing_postcode}
                            </div>
                        </div>
                    </div>

                    <div className="order-summary-details__column">
                        <h2 className="order-summary-details__column__title">
                            Payment
                        </h2>
                        <div className="order-summary-details__copy-block order-summary-details__payment">
                            {data.cybersource_cards.map((card, i) => (
                                <div
                                    key={`cs-${i}`}
                                    className="order-summary-details__cc_payment"
                                >
                                    <span className="order-summary-details__card_type">
                                        {card.card_type_name}
                                    </span>{" "}
                                    -{" "}
                                    <span className="order-summary-details__card_mask">
                                        {card.masked_card_number}
                                    </span>
                                </div>
                            ))}
                            {data.financing_transfers.map((transfer, i) => (
                                <div
                                    key={`fin-${i}`}
                                    className="order-summary-details__financing_payment"
                                >
                                    <div className="order-summary-details__account">
                                        Account:{" "}
                                        {transfer.masked_account_number}
                                    </div>
                                    {transfer.disclosure && (
                                        <span className="order-summary-details__terms">
                                            Terms: {transfer.disclosure}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="order-summary-details__breakdown order-summary-details__column">
                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                Subtotal:
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {data.subtotal_display}
                            </div>
                        </div>

                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                Shipping:
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {data.shipping_display}
                            </div>
                        </div>

                        {data.is_tax_known ? (
                            data.itemized_tax_groups.map((group, i) => (
                                <div
                                    key={i}
                                    className="order-summary-details__breakdown-row"
                                >
                                    <div className="order-summary-details__breakdown-title">
                                        {group.name}
                                    </div>
                                    <div className="order-summary-details__breakdown-number">
                                        {group.amount_display}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="order-summary-details__breakdown-row">
                                <div className="order-summary-details__breakdown-title">
                                    {t`Tax:`}
                                </div>
                                <div className="order-summary-details__breakdown-number">
                                    {t`Tax will be calculated upon shipment`}
                                </div>
                            </div>
                        )}

                        <div className="order-summary-details__breakdown-row">
                            {data.has_discount && (
                                <>
                                    <div className="order-summary-details__breakdown-title">
                                        Discount:
                                    </div>
                                    <div className="order-summary-details__breakdown-number order-summary-details__breakdown-number--cerise">
                                        {data.discount_display}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                Order Total:
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {data.total_display}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
